import type {InputType} from '@Components/input-field-v2/input-field.types';
import type {
  AiGeneratedContent,
  AutoCaptionGenerationResult,
  ErrorType,
  LengthCategory,
  PromptBoxAlterationLoadingText,
  PromptBoxTitles,
  PromptConfigOptions,
  RecentCaptionType,
  RecentlyGeneratedAiContent,
  SelectedValueType,
  SingleContentAiGeneration,
  Tone,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextErrorTypes, HashtagUsage, PopupType, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {countHashtags} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import type {PublishingParamsCommonTextPayload} from '@Components/social-media/publish-params.types';
import {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import type {HasSeenReponse} from '@Components/social-media/social-media.types';
import {GA4EventName} from '@Libraries/ga-events';
import {getNonStoryPlatformMediaType} from '@Libraries/social-media-params.library';

/**
 * @author: Moeed Ahmad <moeed@250mils.com>
 * */
export const AI_CAPTION_INTRO_POPUP_SEEN_CACHE_KEY = 'ai_caption_intro_popup_seen_';

export const getUserHasSeenAiCaptionPopupsStatus = async (): Promise<HasSeenReponse> => {
  const popupsHasSeenStatusInCache = getCachedUserHasSeenAiCaptionPopups();

  if (popupsHasSeenStatusInCache) {
    return popupsHasSeenStatusInCache;
  }

  try {
    const response: HasSeenReponse = (await window.PMW.readLocal(`autocaptions/hasSeenIntroPopup`)) as HasSeenReponse;
    window.PMW.setCacheItem(getAiCaptionPopupsSeenCacheKey(), JSON.stringify(response));
    return response;
  } catch (error) {
    console.error(error);
  }

  return hasSeenStatusDefaultFalse;
};

export const markUserHasSeenPopup = async (popupType: PopupType): Promise<HasSeenReponse> => {
  try {
    const hasSeenResponse = (await window.PMW.writeLocal(`autocaptions/markHasSeenIntroPopup`, {popupType})) as HasSeenReponse;
    window.PMW.setCacheItem(getAiCaptionPopupsSeenCacheKey(), JSON.stringify(hasSeenResponse));
    return hasSeenResponse;
  } catch (error) {
    console.error(error);
    return hasSeenStatusDefaultFalse;
  }
};

export const requestGenerateCaptions = async (
  prompt: InputType,
  options: PromptConfigOptions,
  accountId: number,
  postId: string,
  generateIdeasOriginator: GenerateIdeasOriginator
): Promise<string[]> => {
  try {
    const response = (await window.PMW.readLocal(`autocaptions/generate`, {
      accountId,
      prompt,
      hasHashtags: hasHashtags(options),
      tone: options.tone,
      lengthCategory: options.lengthCategory,
      postId,
      generateIdeasOriginator,
    })) as string[];

    if (generateIdeasOriginator === GenerateIdeasOriginator.Generate) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_FIRSTGEN);
    } else if (generateIdeasOriginator === GenerateIdeasOriginator.MoreIdeas) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_MOREIDEAS);
    }

    return response;
  } catch (e) {
    onErrorAction(e as ErrorType);
    throw e;
  }
};

export const requestGenerateAutoCaptions = async (accountsToPublishTo: SocialAccount[], postId: string): Promise<AutoCaptionGenerationResult | null> => {
  const accountIdsToPublishTo = accountsToPublishTo.map((account) => account.id);
  const accountIdsString = JSON.stringify(accountIdsToPublishTo);
  try {
    const response = (await window.PMW.readLocal(`autocaptions/generateAutoCaptions`, {
      accountIds: accountIdsString,
      postId,
    })) as AutoCaptionGenerationResult;

    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_AUTOCAPTIONGEN);

    return {captionsByAccountId: response.captionsByAccountId ?? {}, hasMultiplePlatformCaptions: response.hasMultiplePlatformCaptions};
  } catch (e) {
    onErrorAction(e as ErrorType);
    return null;
  }
};

export const createGenerateCaptionsFunctionWithContext = (accountId: number, postId: string) => {
  return async (prompt: string, selectedValues: SelectedValueType, originator: GenerateIdeasOriginator): Promise<AiGeneratedContent[]> => {
    const promptConfigOptions = getAutoCaptionPromptConfigOptionsFromSelctedValues(selectedValues);
    const captionResponse = await requestGenerateCaptions(prompt, promptConfigOptions, accountId, postId, originator);
    return captionResponse.map((caption) => {
      return {
        text: caption,
        subtext: '',
        configOptions: selectedValues,
        id: '',
      };
    });
  };
};

/** Skips accounts that already carry text, so a caption the user typed is never overwritten. */
export const buildAutoCaptionPublishingPayload = (
  result: AutoCaptionGenerationResult,
  accountIdsToPublishTo: SocialMediaAccountId[],
  existingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
): PublishingParamsCommonTextPayload => {
  const payload: PublishingParamsCommonTextPayload = {};

  accountIdsToPublishTo.forEach((accountId) => {
    const generatedCaption = result.captionsByAccountId[accountId];

    if (!generatedCaption || hasExistingText(existingParams[accountId])) {
      return;
    }

    payload[accountId] = {
      title: generatedCaption.title,
      caption: generatedCaption.caption,
      areURLsAllowed: false,
    };
  });

  return payload;
};

/**
 * Derived from publishing params rather than tracked generation state, so it survives the details panel unmounting
 * on the account-selection step. Missing params mean the account was only just selected. A stories-only account
 * can never carry a caption, so it never awaits one.
 */
export const hasAccountsAwaitingAutoCaptions = (
  accountIdsToPublishTo: SocialMediaAccountId[],
  existingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
): boolean => {
  return accountIdsToPublishTo.some((accountId) => {
    const params = existingParams[accountId];

    if (!params) {
      return true;
    }

    return getNonStoryPlatformMediaType(params) !== null && !hasExistingText(params);
  });
};

const hasExistingText = (params: MediaTypePublishingParams | undefined): boolean => {
  if (!params) {
    return false;
  }

  return Object.values(params).some((publishingParam) => {
    if (!publishingParam) {
      return false;
    }

    return ('title' in publishingParam && !!publishingParam.title) || ('caption' in publishingParam && !!publishingParam.caption);
  });
};

export const createAutoCaptionGenerationFunction = (accountsToPublishTo: SocialAccount[], postId: string) => {
  return async (): Promise<AutoCaptionGenerationResult | null> => {
    return requestGenerateAutoCaptions(accountsToPublishTo, postId);
  };
};

export const requestUseCaption = async (
  prompt: string,
  selectedValues: SelectedValueType,
  selectedContent: SingleContentAiGeneration,
  accountId: SocialAccount['id'],
  postId: string
): Promise<number> => {
  try {
    const response = (await window.PMW.writeLocal(`autocaptions/useCaption`, {
      caption: selectedContent.text,
      prompt,
      tone: selectedValues[PromptBoxConfigOptionType.TONE],
      lengthCategory: selectedValues[PromptBoxConfigOptionType.LENGTH],
      accountId,
      postId,
    })) as {captionId: number};

    return response.captionId;
  } catch (e) {
    onErrorAction(e as ErrorType);
    throw e;
  }
};

export const createUseFunctionWithContext = (accountId: SocialAccount['id'], postId: string) => {
  return async (prompt: string, selectedValues: SelectedValueType, selectedContent: SingleContentAiGeneration): Promise<number> => {
    return requestUseCaption(prompt, selectedValues, selectedContent, accountId, postId);
  };
};

export const requestReuseCaption = async (captionId: string): Promise<void> => {
  await window.PMW.writeLocal(`autocaptions/reuseCaption`, {captionId});
};

export const requestRecentCaptions = async (): Promise<RecentlyGeneratedAiContent[]> => {
  const recentCaptionResponse = (await window.PMW.readLocal(`autocaptions/getRecent`)) as RecentCaptionType[];
  return recentCaptionResponse.map((recent) => {
    return {
      id: recent.captionid,
      configOptions: {
        [PromptBoxConfigOptionType.TONE]: recent.tone,
        [PromptBoxConfigOptionType.LENGTH]: recent.lengthCategory,
        [PromptBoxConfigOptionType.HASHTAGS]: countHashtags(recent.caption) ? HashtagUsage.USE_HASHTAGS : HashtagUsage.NO_HASHTAGS,
      },
      text: recent.caption,
      subtext: '',
    };
  });
};

export const requestResetAiCaptionInsights = async (postId: string | null, accountIds: number[] | null): Promise<void> => {
  await window.PMW.readLocal(`autocaptions/resetInsights`, {
    postId,
    accountIds,
  });
};

const hasHashtags = (options: PromptConfigOptions): boolean => {
  return options.hasHashtags === HashtagUsage.USE_HASHTAGS;
};

const getAiCaptionPopupsSeenCacheKey = (): string => {
  return AI_CAPTION_INTRO_POPUP_SEEN_CACHE_KEY + window.PMW.getUserId().toString();
};

const getCachedUserHasSeenAiCaptionPopups = (): HasSeenReponse | null => {
  const cachedHasSeenStatus = JSON.parse(window.PMW.getCacheItem(getAiCaptionPopupsSeenCacheKey()) as string) as HasSeenReponse;

  if (!cachedHasSeenStatus) {
    return null;
  }

  return {
    [PopupType.INTRO]: cachedHasSeenStatus[PopupType.INTRO] ?? false,
    [PopupType.REMINDER]: cachedHasSeenStatus[PopupType.REMINDER] ?? false,
  };
};

/**
 * based on Operations enum in Operations.php for AutoCaptions controller
 * */
export const hasSeenStatusDefaultFalse: HasSeenReponse = {
  [PopupType.INTRO]: false,
  [PopupType.REMINDER]: false,
};

export const getPromptBoxTitles = (isInMobileMode = false): PromptBoxTitles => {
  return {
    selected: window.i18next.t('pmwjs_selected_caption'),
    recents: isInMobileMode ? window.i18next.t('pmwjs_history') : window.i18next.t('pmwjs_recently_used_captions'),
    main: isInMobileMode ? window.i18next.t('pmwjs_caption_ideas') : window.i18next.t('pmwjs_ai_captions'),
    primarySelectedContent: window.i18next.t('pmwjs_caption'),
  };
};

export const getAlterationLoadingText = (): PromptBoxAlterationLoadingText => {
  return {
    primaryAlterationLoadingMessage: window.i18next.t('pmwjs_updating_caption'),
  };
};

export const getAutoCaptionPromptConfigOptionsFromSelctedValues = (selectedValues: SelectedValueType): PromptConfigOptions => {
  return {
    tone: selectedValues[PromptBoxConfigOptionType.TONE] as Tone,
    lengthCategory: selectedValues[PromptBoxConfigOptionType.LENGTH] as LengthCategory,
    hasHashtags: selectedValues[PromptBoxConfigOptionType.HASHTAGS] as HashtagUsage,
  };
};

const onErrorAction = (error: ErrorType): void => {
  const {code} = error;

  if ((code as AiTextErrorTypes) === AiTextErrorTypes.LimitReached) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_LIMIT_REACHED);
  }
};
